import {Injectable, Optional} from '@angular/core';
import {reportAccountAttributionOnce} from "../common/attribution";
import {NavigationEnd, Router} from '@angular/router';
import {
    appData, compareIds, DEV_TEST_PORT, formatAccountDetails, isHostDev,
    isHostDevOrLocal, isMigrationAvailable, isAccountMigratable, isNullOrUndefOrEmpty, isSubscribersMode, userTemplate,
} from "app/common";
import {BehaviorSubject, Observable, of} from 'rxjs';
import {EnumLoginStatus, EnumLoginViewMode, EnumMigrationStatus, EnumStudioType, IAccountReplyV2, IAccountRequest, IAppStatic, IUser, IUserSync, serverCallType} from "../interfaces";
import {actionAccountCommandsRequest, actionAccountSocialLoginReply, actionAccountSocialLoginRequest, actionLoginViewMode, actionPreferenceUpdateStore, actionSelectedUserUpdate, actionPreferencesGet, actionUpdateAccountDetails} from "../store/actions/user.actions";
import {StoreSelectorsService} from "./store-selectors.service";
import {LoginForm} from "../models";
import {timeout} from "../decorators/timeout";
import {SocialAuthService, SocialUser} from "@abacritt/angularx-social-login";
import {ToastrService} from "ngx-toastr";
import {LocalStoreService} from "./local-store.service";
import {GoogleIdentityService} from "./google-identity.service";
import {TranslocoService} from "@ngneat/transloco";
import {IGroupsPrivileges} from "../interfaces/enterprise-interface";
import {filter, take} from 'rxjs/operators';
import {IAmazonLogin, ISocialServerReply} from "../interfaces/social-interface";
import {Lib} from "../../libs/Lib";
import * as _ from 'lodash';
import {Compbaser} from "../components/compbaser";
import {staticAccountTemplates} from "../../assets/accountTemplates/acounts-constants";

const {localStoreConfigVersion: localStoreConfigVersion} = require('../../../package.json');

/**
 * AuthUserService - User Authentication & Session Management
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * OVERVIEW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * AuthUserService is the central authentication service managing:
 * - User login/logout operations (traditional and social)
 * - Session persistence via local storage
 * - User account creation
 * - Login status tracking and navigation
 * - Multi-studio type routing (LITE, PRO, SIGN_STUDIO)
 * - Remember me functionality
 * - Social authentication (Google, Facebook, Microsoft, Amazon)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * KEY ARCHITECTURAL PATTERNS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. LOGIN FLOW (Traditional)
 *    login() -> loginInProgress() -> dispatch actionAccountCommandsRequest
 *    -> Server validates -> loginSuccess() -> dispatchUser() -> navigate()
 *
 * 2. SOCIAL LOGIN FLOW
 *    SocialAuthService detects login -> createUserSocial() checks if exists
 *    -> If new: createUser() -> accountCreated() -> dispatchUser() -> navigate()
 *    -> If existing: Standard login flow
 *
 * 3. SESSION PERSISTENCE
 *    - "Remember me" = true: User saved to localStorage
 *    - On app load: getUserFromStorage() -> rewind() -> login()
 *    - Version check: localStoreConfigVersion must match to prevent stale data
 *    - Dev mode: Skip re-authentication, use cached user directly
 *
 * 4. USER TYPES & ROUTING
 *    - EnumStudioType.SIGN_STUDIO: Full SignStudio -> /home
 *    - EnumStudioType.PRO: Legacy Pro Studio -> /getstarted/downloads/studio
 *    - EnumStudioType.LITE: Legacy Lite Studio -> /getstarted/downloads/studio
 *    - Dashboard users (user.dashboard = true): Enterprise reseller dashboard
 *    - Regular users (user.dashboard = false): Standard business account
 *
 * 5. SERVER CALL TYPES (serverCallType)
 *    - "VERIFY_V2": Standard login verification
 *    - "SYNC_USER": Background user sync (updates account status)
 *    - "CREATE": New account creation
 *    - "STATION_MONITOR": Login for station monitoring mode
 *    - "SUBSCRIBE_STANDALONE": Subscriber mode (token-based, no localStorage)
 *
 * 6. LOGIN STATUS (EnumLoginStatus)
 *    - null/undefined: Not yet attempted
 *    - LOGIN_PASSED: Successful authentication
 *    - LOGIN_FAILED: Invalid credentials or server error
 *    - LOGOUT: User explicitly logged out
 *
 * 7. LOGIN VIEW MODE (EnumLoginViewMode)
 *    - INIT: Initial state, checking for saved session
 *    - LOGIN: Show login form
 *    - LOGIN_IN_PROGRESS: Authenticating with server
 *    - CREATE_ACCOUNT: Show account creation form
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * USER DATA STRUCTURE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * IUser contains:
 * - Authentication: username, passwordNew, rememberMe, accessToken
 * - Business: businessId, businessName, businessDomain, resellerId
 * - Account: accountStatus, accountStatusReseller, payerId, enterprise
 * - Studio: studioType, dashboard, customStudio, applicationId
 * - API URLs: webApiBaseUrl, webApiResellerBaseUrl, adnetUrls
 * - Permissions: accessMask, privilegeId, groupsPrivileges
 * - Metadata: createdAt, updatedAt, loginStatus, localStoreConfigVersion
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * INITIALIZATION FLOW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * constructor()
 *   ├─> Check isSubscribersMode() - if true, disable localStorage
 *   ├─> dispatch actionLoginViewMode(INIT)
 *   ├─> rewind() - Check for saved user in localStorage
 *   ├─> Subscribe to router events - track currentUrl
 *   ├─> Subscribe to appSettings - save configuration
 *   └─> Subscribe to resellerId - track current reseller
 *
 * rewind() [on app load]
 *   ├─> getUserFromStorage()
 *   ├─> Check localStoreConfigVersion - if mismatch, clear storage
 *   ├─> Dev mode: Use cached user directly (skip re-auth)
 *   └─> Production: Re-authenticate with stored credentials
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SOCIAL AUTHENTICATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Supported Providers:
 * - Google (OAuth 2.0)
 * - Facebook (OAuth 2.0)
 * - Microsoft (Azure AD / LDAP)
 * - Amazon (OAuth 2.0)
 *
 * Flow:
 * 1. User clicks social login button
 * 2. SocialAuthService handles OAuth flow
 * 3. getSocialAuthState$() observable emits SocialUser
 * 4. createUserSocial() checks if account exists
 * 5. If new: createUser() with random password
 * 6. If existing: loginSuccess() with existing credentials
 *
 * Notes:
 * - Social accounts get random generated passwords
 * - Email from social provider becomes username
 * - socialSignOut() clears both local and social auth state
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LOCAL STORAGE MANAGEMENT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Stored Data:
 * - IUser: Complete user object (if rememberMe = true)
 * - localStoreConfigVersion: Version check for compatibility
 * - Preferences: User settings and configurations
 * - App cache: Service worker cache
 *
 * Cleanup:
 * - removeUserFromStorage(): Clears IUser only
 * - removeSettingsFromStorage(): Clears preferences
 * - removeAppCache(): Clears service worker cache
 * - logout(): Calls all three cleanup methods
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ACCOUNT CREATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * createUser() Parameters:
 * - businessName: Company name (defaults to "DigitalSignage")
 * - userName: Email address (must be valid email)
 * - password: User password (auto-generated for social login)
 * - contactEmail: Contact email (usually same as userName)
 * - templateSampleId: Template account to clone resources from
 * - resellerId: Reseller/white-label identifier
 * - studioType: EnumStudioType (LITE, PRO, SIGN_STUDIO)
 *
 * Flow:
 * 1. createUser() dispatches actionAccountCommandsRequest with CREATE
 * 2. Server creates account and copies template resources
 * 3. accountCreated() called with credentials and server reply
 * 4. updateUserSharedFields() populates IUser object
 * 5. dispatchUser() updates NgRx store
 * 6. navigate() routes to appropriate starting page
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * COMMON PITFALLS & TROUBLESHOOTING
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. User keeps getting logged out?
 *    - Check localStoreConfigVersion in package.json
 *    - Verify localStorage is not disabled
 *    - Check rememberMe flag is true
 *
 * 2. Social login not working?
 *    - Verify SocialAuthService is injected (@Optional decorator)
 *    - Check getSocialAuthService() returns valid instance
 *    - Verify social provider OAuth configuration
 *
 * 3. Wrong navigation after login?
 *    - Check user.studioType value
 *    - Verify navigate() switch statement logic
 *    - Check currentUrl for checkout/shopping overrides
 *
 * 4. Login succeeds but user data missing?
 *    - Check updateUserSharedFields() mapping
 *    - Verify server reply structure (IAccountReplyV2)
 *    - Check if dashboard vs regular user (affects data mapping)
 *
 * 5. Can't log in on dev server?
 *    - Check isHostDevOrLocal() detection
 *    - Verify DEV_TEST_PORT configuration
 *    - Check webApiBaseUrl override logic (lines 244-250)
 */
@Injectable()
export class AuthUserService extends Compbaser {

    /** BehaviorSubject tracking current authenticated user (null = not logged in) */
    readonly user$: BehaviorSubject<IUser | null> = new BehaviorSubject(null);

    /** URL to redirect to after successful login (set by AuthGuard) */
    redirectUrl: string | null = null;

    /** Current router URL (tracked for checkout/shopping overrides) */
    currentUrl: string;

    /** Current reseller ID (for white-label configuration) */
    resellerId: any;

    /** Static app configuration (URLs, settings, etc.) */
    appSettings: IAppStatic;

    /** Flag to enable/disable localStorage usage (false in subscriber mode) */
    loadLocalStorage = true;

    constructor(
        private translocoService: TranslocoService,
        private router: Router,
        private toastr: ToastrService,
        private localStorage: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        private googleIdentity: GoogleIdentityService,
        @Optional() private authService: SocialAuthService) {

        super();

        /** when loading in standalone subscriber mode we ignore local storage as all data will come from token / network **/
        if (isSubscribersMode()) {
            this.loadLocalStorage = false;
        }

        this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.INIT}));
        this.rewind();
        this.cod(
            this.router.events
                .pipe(
                    filter(event => event instanceof NavigationEnd)
                ).subscribe((event: NavigationEnd) => {
                    this.currentUrl = event.url;
                }
            )
        );
        this.cod(
            storeSelector.getAppSettings()
                .subscribe((appSettings: IAppStatic) => {
                    this.appSettings = appSettings;
                })
        );
        this.cod(
            this.storeSelector.getResellerId(this.localStorage)
                .subscribe((i_resellerId: number) => {
                    this.resellerId = i_resellerId;
                })
        );
    }

    /**
     * Get Social Authentication State Observable
     *
     * Returns observable that emits SocialUser when social login state changes.
     * Used to detect when user logs in via Google, Facebook, Microsoft, or Amazon.
     *
     * @returns Observable<SocialUser> - Emits on social auth state changes
     *          Returns empty observable in local debug mode (no social auth)
     */
    getSocialAuthState$(): Observable<SocialUser> {
        if (this.authService && this.authService?.authState) return this.authService.authState;
        return of(); // debug local mode
    }

    /**
     * Get Social Authentication Service
     *
     * Returns the SocialAuthService instance for programmatic social login operations.
     * Service is @Optional, so may be undefined in certain configurations.
     *
     * @returns SocialAuthService | undefined
     */
    getSocialAuthService(): SocialAuthService {
        if (this.authService) return this.authService;
    }

    /**
     * Social Sign Out
     *
     * Clears social authentication state from both:
     * 1. NgRx store (actionAccountSocialLoginRequest/Reply)
     * 2. GoogleIdentityService (Google) and SocialAuthService (other providers)
     *
     * Called during logout() to ensure complete session cleanup.
     * Safe to call even if user didn't use social login (no-op).
     */
    socialSignOut(): void {
        this.storeSelector.dispatch(actionAccountSocialLoginRequest({payload: null}));
        this.storeSelector.dispatch(actionAccountSocialLoginReply({payload: null}));
        this.googleIdentity.signOut();
        if (!this.authService) return console.log('Social login not loaded');
        this.authService.authState.pipe(take(1)).subscribe((v) => {
            if (v) {
                this.authService.signOut().catch().finally();
            }
        });
    }

    /**
     * Save User to Local Storage
     *
     * Persists user object to localStorage for "remember me" functionality.
     * Only called when rememberMe = true and loadLocalStorage = true.
     *
     * @param model - IUser object to save
     */
    private setUserIntoStorage(model: IUser): void {
        this.localStorage.setItem('IUser', model);
    }

    /**
     * Get User from Local Storage
     *
     * Retrieves saved user from localStorage on app initialization.
     * Performs validation checks before returning user:
     * 1. User exists
     * 2. localStoreConfigVersion matches (prevent stale data)
     * 3. Username length >= 3
     * 4. Password length >= 3
     *
     * Behavior:
     * - Invalid/missing user: Clear storage, show login form
     * - Valid user + Dev mode: Use cached user directly (skip re-auth)
     * - Valid user + Production: Re-authenticate with stored credentials
     *
     * @returns IUser | null - User if valid, null if invalid/missing
     */
    private getUserFromStorage(): IUser | null {
        const user: IUser = this.localStorage.getItem('IUser', null);
        if (!user || user?.localStoreConfigVersion !== localStoreConfigVersion || user?.username.length < 3 || user?.passwordNew.length < 3) {
            this.removeUserFromStorage();
            this.dispatchUser(null);
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
            return null;
        } else {
            /** if in dev mode we re-load the same router path as before for easy development **/
            if (Lib.DevMode()) {

                // test only updating accountStatus after login
                // user.accountStatus = 4;
                // user.accountStatusReseller = 4;
                // setTimeout(() => {
                //     this.appService.syncUser();
                // }, 5000);

                this.dispatchUser(user);
                return user;
            }
            const m: LoginForm = {username: user.username, password: user.passwordNew};
            this.login(m, true);
            this.dispatchUser(null);
            this.removeUserFromStorage();
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN_IN_PROGRESS}));
            return null;
        }
    }

    /** Remove user object from localStorage (called on logout or invalid session) */
    public removeUserFromStorage(): void {
        this.localStorage.removeUserStorage();
    }

    /** Remove user preferences/settings from localStorage */
    private removeSettingsFromStorage(): void {
        this.localStorage.removeSettingsFromStorage();
    }

    /** Clear service worker cache (called on logout for complete cleanup) */
    private removeAppCache(): void {
        this.localStorage.unCacheApp();
    }

    /**
     * Login - Entry Point for User Authentication
     *
     * Main login method called from login form submission.
     * Dispatches authentication request to server via NgRx actions.
     *
     * @param model - LoginForm containing username and password
     * @param i_rememberMe - If true, save user to localStorage after successful login
     *
     * Special case: Empty username triggers logout() instead
     */
    public login(model: LoginForm, i_rememberMe: boolean): void {
        if (model.username === '') {
            this.logout();
        } else {
            this.loginInProgress(model, i_rememberMe, 'VERIFY_V2');
        }
    }

    /**
     * Login In Progress - Dispatch Authentication Request
     *
     * Dispatches actionAccountCommandsRequest to NgRx store, which triggers
     * an effect that calls the server authentication API.
     *
     * @timeout(1000) decorator ensures minimum 1s delay between login attempts
     *
     * @param model - LoginForm with credentials
     * @param rememberMe - Save session flag
     * @param i_serverCallType - Type of server call:
     *   - "VERIFY_V2": Standard login
     *   - "STATION_MONITOR": Station monitoring mode
     *   - "SYNC_USER": Background sync
     *   - "SUBSCRIBE_STANDALONE": Subscriber mode
     */
    @timeout(1000)
    public loginInProgress(model: LoginForm, rememberMe: boolean, i_serverCallType: serverCallType) {
        this.storeSelector.appStore.dispatch(actionAccountCommandsRequest({
                accountRequest: {
                    verifyAccount: {
                        userName: model.username,
                        password: model.password,
                        rememberMe
                    },
                    serverCall: i_serverCallType
                }
            })
        );
    }

    /**
     * Update User Shared Fields
     *
     * Core method that maps server reply (IAccountReplyV2) to IUser object.
     * Used by both loginSuccess() and accountCreated() to populate user data.
     *
     * Mapped Fields:
     * - Account status (user and reseller)
     * - Business info (ID, name, domain, reseller)
     * - Dashboard mode (enterprise reseller vs regular user)
     * - Custom studio configuration
     * - Access permissions (mask, privileges, groups)
     * - Account limits (max monitors, storage)
     * - Billing (payer ID, token)
     *
     * @param i_serverReply - Server authentication response
     * @param i_userTemplate - Template to clone (usually empty userTemplate)
     * @returns IUser - Fully populated user object
     */
    protected updateUserSharedFields(i_serverReply: IAccountReplyV2, i_userTemplate: IUser): IUser {
        const userTemplateCopy = _.cloneDeep(i_userTemplate);
        userTemplateCopy.accountStatus = Lib.GetAccountStatus(i_serverReply);
        userTemplateCopy.name = i_serverReply.businessUserInfo?.name;
        userTemplateCopy.accountStatusReseller = i_serverReply.businessUserInfo?.resellerInfo?.accountStatus;
        userTemplateCopy.dashboard = !i_serverReply.businessUserInfo.businessInfo;
        userTemplateCopy.customStudio = i_serverReply.businessUserInfo?.resellerInfo?.customStudio;
        userTemplateCopy.webToken = i_serverReply.businessUserInfo?.businessInfo?.webToken;
        userTemplateCopy.loginStatus = EnumLoginStatus.LOGIN_PASSED;
        userTemplateCopy.businessName = this.getBusinessName(i_serverReply);
        userTemplateCopy.updatedAt = userTemplateCopy.dashboard ? '' : i_serverReply.businessUserInfo.businessInfo.lastLogin;
        userTemplateCopy.createdAt = userTemplateCopy.dashboard ? '' : i_serverReply.businessUserInfo.businessInfo.loginTime;
        userTemplateCopy.payerId = userTemplateCopy.dashboard ? Number(i_serverReply.businessUserInfo?.resellerInfo?.payerId) : Number(i_serverReply.businessUserInfo?.businessInfo?.payerId);
        userTemplateCopy.archiveState = Number(i_serverReply.businessUserInfo?.businessInfo?.archiveState);
        userTemplateCopy.sourceId = Number(i_serverReply.businessUserInfo?.businessInfo?.sourceId);
        userTemplateCopy.fromTemplateId = Number(i_serverReply.businessUserInfo?.businessInfo?.fromTemplateId);
        userTemplateCopy.maxMonitors = Number(i_serverReply.businessUserInfo?.businessInfo?.maxMonitors);
        userTemplateCopy.maxDataStorage = Number(i_serverReply.businessUserInfo?.businessInfo?.maxDataStorage);
        userTemplateCopy.allowSharing = Boolean(i_serverReply.businessUserInfo?.businessInfo?.allowSharing);
        userTemplateCopy.applicationId = userTemplateCopy.dashboard ? '-1' : String(i_serverReply.businessUserInfo.businessInfo.applicationId);
        userTemplateCopy.businessId = userTemplateCopy.dashboard ? userTemplateCopy.businessId = i_serverReply.businessUserInfo.resellerInfo.businessId : i_serverReply.businessUserInfo.businessInfo.businessId;
        userTemplateCopy.resellerId = userTemplateCopy.dashboard ? userTemplateCopy.businessId = i_serverReply.businessUserInfo.resellerInfo.businessId : i_serverReply.businessUserInfo.businessInfo.resellerId;
        userTemplateCopy.enterprise = userTemplateCopy.dashboard ? true : !compareIds(userTemplateCopy.resellerId, '1');
        // per-user migration permission (business_users.allow_migration). Only white-label accounts
        // are gated on it; direct/free accounts always migrate. Used by the /migrate route guard.
        // NOTE: GetBusinessUserInfo serves this over XML as the string "1"/"0", and a NULL column
        // (never granted) serializes to "0". Boolean("0") is TRUE in JS, so a plain Boolean() cast
        // wrongly marked un-granted accounts as allowed — compare the string explicitly instead.
        userTemplateCopy.allowMigration = i_serverReply.businessUserInfo?.allowMigration === '1'
            || i_serverReply.businessUserInfo?.allowMigration === true;
        userTemplateCopy.pricingMethod = i_serverReply.businessUserInfo?.resellerInfo?.pricingMethod;
        userTemplateCopy.localStoreConfigVersion = localStoreConfigVersion;
        userTemplateCopy.accessMask = i_serverReply.businessUserInfo.accessMask;
        userTemplateCopy.privilegeId = i_serverReply.businessUserInfo.privilegeId;
        userTemplateCopy.groupsPrivileges = i_serverReply.businessUserInfo?.privilegeInfo?.Groups ? i_serverReply.businessUserInfo.privilegeInfo.Groups as IGroupsPrivileges : null;
        userTemplateCopy.billingToken = i_serverReply.businessUserInfo?.billingToken;

        return userTemplateCopy;
    }

    public accountCreated(user, pass, serverReply: IUserSync) {
        const userTemplateCopy = this.updateUserSharedFields(serverReply.accountReplyV2, userTemplate);
        userTemplateCopy.studioType = serverReply.accountReplyV2.businessUserInfo.businessInfo.studioType;
        userTemplateCopy.migrationStatus = Number(serverReply.accountReplyV2.businessUserInfo.businessInfo.migrationStatus) || EnumMigrationStatus.NONE;
        userTemplateCopy.migrationDate = serverReply.accountReplyV2.businessUserInfo.businessInfo.migrationDate || '';
        userTemplateCopy.webApiBaseUrl = `${appData.protocol}${serverReply.accountReplyV2.businessUserInfo.businessDomain}`;
        userTemplateCopy.businessDomain = serverReply.accountReplyV2.businessUserInfo.businessDomain;
        userTemplateCopy.username = user;
        userTemplateCopy.email = user;
        userTemplateCopy.passwordNew = pass;
        this.testOverrides(userTemplateCopy);
        this.dispatchUser(userTemplateCopy);
        this.replace(userTemplateCopy);
        this.navigate(userTemplateCopy);
    }

    testOverrides(i_userTemplateCopy) {
        if (Lib.DevMode()) {
            // i_userTemplateCopy.enterprise = true;
            // i_userTemplateCopy.accountStatus = ACCOUNT_STATUS['subscriber'];
            // i_userTemplateCopy.dashboard = true;
            // i_userTemplateCopy.studioType = EnumStudioType.SIGN_STUDIO;
        }
        Lib.DevShow(`Login enterprise[${i_userTemplateCopy.enterprise}] dashboard[${i_userTemplateCopy.dashboard}] studioType[${i_userTemplateCopy.studioType}]`);
    }

    /**
     * Login Success - Handle Successful Authentication
     *
     * Called by NgRx effects when server authentication succeeds.
     * This is the main success handler for all login types.
     *
     * Flow:
     * 1. Map server reply to IUser via updateUserSharedFields()
     * 2. Populate authentication fields (username, password, email)
     * 3. Build API URLs (webApiBaseUrl, webApiResellerBaseUrl, adnetUrls)
     * 4. Handle dev server URL overrides
     * 5. Save to localStorage if rememberMe = true
     * 6. Dispatch user to NgRx store
     * 7. Route to appropriate page based on serverCallType
     *
     * Server Call Type Routing:
     * - "VERIFY_V2": Standard login -> replace() -> navigate()
     * - "STATION_MONITOR": Station monitoring -> /monitor/poll
     * - "SUBSCRIBE_STANDALONE": Subscriber mode -> no navigation
     * - "SYNC_USER": Background sync -> update account details only
     *
     * @param userSync - Server reply with user and account data
     * @param request - Original login request (contains credentials)
     * @param i_serverCallType - Type of authentication call
     */
    loginSuccess(userSync: IUserSync, request: IAccountRequest, i_serverCallType: serverCallType) {
        const userTemplateCopy = this.updateUserSharedFields(userSync.accountReplyV2, userTemplate);
        const isEnterpriseDashboard = !userSync.accountReplyV2.businessUserInfo.businessInfo;
        userTemplateCopy.studioType = isEnterpriseDashboard ? EnumStudioType.SIGN_STUDIO : userSync.accountReplyV2.businessUserInfo.businessInfo.studioType;
        userTemplateCopy.migrationStatus = isEnterpriseDashboard
            ? EnumMigrationStatus.NONE
            : (Number(userSync.accountReplyV2.businessUserInfo.businessInfo.migrationStatus) || EnumMigrationStatus.NONE);
        userTemplateCopy.migrationDate = isEnterpriseDashboard ? '' : (userSync.accountReplyV2.businessUserInfo.businessInfo.migrationDate || '');
        userTemplateCopy.loginStatus = EnumLoginStatus.LOGIN_PASSED;
        userTemplateCopy.email = request.verifyAccount.userName;
        userTemplateCopy.username = request.verifyAccount.userName;
        userTemplateCopy.passwordNew = request.verifyAccount.password;
        userTemplateCopy.ip = userSync?.ip;
        userTemplateCopy.rememberMe = request.verifyAccount.rememberMe;
        userTemplateCopy.adnetUrls = {
            request: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=customerRequest&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&fromChangelistId=0`,
            billing: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=:BILLING_TYPE:&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&:DATA:`,
            report: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=:REPORT_TYPE:&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&data=:DATA:`,
            save: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=customerSubmit&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID:&data=:DATA:`,
            search: `${this.appSettings.appBaseUrlsAdnet}/adNetService.ashx?command=search&customerId=:ADNET_CUSTOMER_ID:&customerToken=:ADNET_TOKEN_ID::DATA:`
        };
        userTemplateCopy.webApiResellerBaseUrl = `${appData.protocol}${appData.masterServer}/WebService/ResellerService.ashx?resellerUserName=${userTemplateCopy.email}&resellerPassword=${Lib.Base64().encode(userTemplateCopy.passwordNew)}&base64=1`;
        userTemplateCopy.businessDomain = isEnterpriseDashboard ? '' : userSync.accountReplyV2.businessUserInfo.businessDomain;
        userTemplateCopy.webApiBaseUrl = isEnterpriseDashboard ? '' : `${appData.protocol}${userTemplateCopy.businessDomain}`;

        // in development use dev C# server
        if (isHostDevOrLocal()) {
            if (userTemplateCopy.webApiBaseUrl === "https://ida.signage.me") {
                userTemplateCopy.webApiBaseUrl = `https://ida.signage.me:${DEV_TEST_PORT}`;
                console.log(`>>> dev server https://ida.signage.me:${DEV_TEST_PORT}`);
            }
        }
        console.log('Account user version ' + Lib.GetAccountVersion(userTemplateCopy));

        // Load preferences (including 2FA state) for reseller/enterprise dashboard users via mars
        if (isEnterpriseDashboard) {
            this.storeSelector.dispatch(actionPreferencesGet({user: userTemplateCopy, globalService: this.appSettings.globalService, checkTwoFactor: true}));
        }

        if (userTemplateCopy.enterprise) {
            this.localStorage.setPreloginReseller(userTemplateCopy.resellerId);
        }
        this.testOverrides(userTemplateCopy);

        switch (i_serverCallType) {
            case "VERIFY_V2": {
                this.replace(userTemplateCopy);
                if (request.verifyAccount.rememberMe) {
                } else {
                    this.removeUserFromStorage();
                }
                this.dispatchUser(userTemplateCopy);
                this.navigate(userTemplateCopy);
                break;
            }
            case "STATION_MONITOR_WALL": {
                // Monitor Wall v2 login — stays on the current /monitor-wall/:token route.
                this.replace(userTemplateCopy);
                this.dispatchUser(userTemplateCopy);
                break;
            }
            case "SUBSCRIBE_STANDALONE": {
                // this.replace(userTemplateCopy);
                this.dispatchUser(userTemplateCopy);
                // this.router.navigateByUrl('/monitor/poll');
                break;
            }
            case "SYNC_USER": {
                this.replace(userTemplateCopy);
                this.dispatchUser(userTemplateCopy);
                this.storeSelector.dispatch(actionUpdateAccountDetails({userDetails: formatAccountDetails(userSync.accountDetails)}));
                break;
            }
        }
    }

    dispatchUser(user) {
        this.storeSelector.logger({message: `user logged in ${userTemplate.email}`, severity: 0});
        this.storeSelector.dispatch(actionSelectedUserUpdate({user}));
        // 2026-09-27: first-touch attribution (mars keeps it only for accounts < 30 days old, insert-once),
        // on every login so a signup whose create pipeline skipped the report is still captured.
        if (user && user.businessId) reportAccountAttributionOnce(this.appSettings && this.appSettings.globalService, user.businessId);
    }

    /**
     * Locally set the current account's studioType (e.g. after a migration flips it to
     * SignStudio). Updates the NgRx store AND the persisted localStorage IUser so a page reload
     * hydrates with the new type instead of the stale cached value. Client-side only — the
     * authoritative server flip is done separately (MasterService setStudioType); this just keeps
     * the browser cache coherent so the migration UI doesn't reappear until the next server sync.
     */
    public setLocalStudioType(i_studioType: EnumStudioType): void {
        const current: IUser = this.user$?.value || this.localStorage.getItem('IUser', null);
        if (!current) return;
        const updated: IUser = {...current, studioType: i_studioType};
        this.dispatchUser(updated);  // NgRx store (re-evaluates migration gating this session)
        this.replace(updated);       // persist to localStorage + user$ (survives the reload)
    }

    /**
     * Locally set the account's migrationStatus (0 none / 1 in progress / 2 complete), mirroring
     * setLocalStudioType. Used after an interrupted migration is cleaned up (status back to 0) so the
     * page stops offering "Continue Migration" immediately, instead of until the next server sync.
     * Client-side only — the authoritative write is MasterService setMigrationStatus.
     */
    public setLocalMigrationStatus(i_status: EnumMigrationStatus): void {
        const current: IUser = this.user$?.value || this.localStorage.getItem('IUser', null);
        if (!current) return;
        const updated: IUser = {...current, migrationStatus: i_status};
        this.dispatchUser(updated);
        this.replace(updated);
    }

    getBusinessName(i_serverReply: IAccountReplyV2): string {
        try {
            if (i_serverReply?.businessUserInfo?.resellerInfo) {
                if (!isNullOrUndefOrEmpty(i_serverReply?.businessUserInfo?.resellerInfo.name)) {
                    return i_serverReply.businessUserInfo.resellerInfo.name;
                }
                if (!isNullOrUndefOrEmpty(i_serverReply?.businessUserInfo?.businessInfo?.name)) {
                    return i_serverReply.businessUserInfo.businessInfo.name;
                }
                const user: IUser = this.localStorage.getItem('IUser', null);
                if (!user || user?.localStoreConfigVersion !== localStoreConfigVersion || user?.username.length < 3 || user?.passwordNew.length < 3) {
                    if (!isNullOrUndefOrEmpty(user.businessName)){
                        return user.businessName;
                    }
                    return 'DigitalSignage';
                }
            } else {
                return 'DigitalSignage';
            }
        } catch (e) {
            return 'DigitalSignage';
        }
    }

    /**
     * Logout - Complete Session Cleanup
     *
     * Performs full logout sequence:
     * 1. Save remote config to localStorage (preserves some settings)
     * 2. Remove user from localStorage
     * 3. Remove settings/preferences
     * 4. Clear app cache (service worker)
     * 5. Sign out of social auth providers
     * 6. Dispatch empty user with LOGOUT status
     *
     * User will be redirected to login page by route guards.
     */
    logout(): void {
        /** immediate: callers navigate away right after logout, the push must leave before that **/
        this.localStorage.setRemoteLocalStorageConfig(true);
        this.removeUserFromStorage();
        this.removeSettingsFromStorage();
        this.removeAppCache();
        this.socialSignOut();
        const user: IUser = this.getEmptyUser();
        user.loginStatus = EnumLoginStatus.LOGOUT;
        this.user$.next(user);
    }

    /**
     * Login Failed - Handle Authentication Failure
     *
     * Called when server returns authentication error.
     * Cleans up any partial login state and shows error message.
     *
     * Actions:
     * - Remove user from storage
     * - Sign out of social auth
     * - Clear resource copy tracking
     * - Show error toast
     * - Reset to login view mode
     */
    loginFailed() {
        this.removeUserFromStorage();
        this.socialSignOut();
        const user: IUser = this.getEmptyUser();
        user.loginStatus = EnumLoginStatus.LOGIN_FAILED;
        this.user$.next(user);
        this.localStorage.setStorageValConfig('accountInitialCopyResources', '');
        this.toastr.error('login failed, please contact live chat');
        this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
    }

    /**
     * Replace - Save User to Storage and Stream
     *
     * Updates both localStorage and user$ BehaviorSubject.
     * Only saves to localStorage if loadLocalStorage flag is true.
     *
     * Called after successful login to persist session.
     *
     * @param model - IUser to save
     */
    replace(model: IUser): void {
        if (this.loadLocalStorage) {
            this.setUserIntoStorage(model);
            this.user$.next(model);
        }
    }

    /**
     * Rewind - Restore Session from Storage
     *
     * Called on app initialization to check for saved session.
     * If valid user found in localStorage, emits to user$ stream.
     *
     * getUserFromStorage() handles validation and re-authentication.
     */
    rewind(): void {
        // when launched with ?autologin=..., skip restoring any stored (e.g. admin) session so the
        // autologin credentials take over instead of the saved session silently auto-resuming
        if (this.hasAutologinParam()) {
            this.dispatchUser(null);
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
            return;
        }
        if (this.loadLocalStorage) {
            this.user$.next(this.getUserFromStorage());
        }
    }

    private hasAutologinParam(): boolean {
        try {
            return new URLSearchParams(window.location.search).has('autologin');
        } catch (e) {
            return false;
        }
    }

    /**
     * Navigate - Route After Successful Login
     *
     * Routes user to appropriate page based on:
     * 1. Current URL (checkout/shopping have priority)
     * 2. Studio type (LITE, PRO, SIGN_STUDIO)
     *
     * Routing Logic:
     * - checkout URL: Always route to /shopping-cart/checkout
     * - shopping URL: Stay on current shopping page
     * - LITE/PRO: Route to /getstarted/downloads/studio
     * - SIGN_STUDIO: Route to /home (or navigateOverview())
     *
     * Note: LITE studio routing logic disabled as of 3-17-2024,
     *       now falls through to same handling as PRO.
     *
     * @param i_user - Authenticated user object
     */
    navigate(i_user: IUser): void {
        if (this.currentUrl && this.currentUrl.toLowerCase().indexOf('checkout') > -1) {
            this.router.navigateByUrl('/shopping-cart/checkout');
            return;
        }
        if (this.currentUrl && this.currentUrl.toLowerCase().indexOf('shopping') > -1) {
            return;
        }
        if (this.currentUrl && this.currentUrl.toLowerCase().indexOf('auth-callback') > -1) {
            return;
        }
        switch (i_user.studioType) {
            case EnumStudioType.LITE: {

                /**
                 disabled 3-17-2024, now will fall through same as [ case EnumStudioType.PRO ]:
                 this.toastr.error(this.translocoService.translate('toast.redirectLite', {}, this.translocoService.getActiveLang()));
                 this.logout();
                 window.location.replace(this.appSettings.studioLiteLegacy);
                 this.logout();
                 **/

                this.router.navigateByUrl('/getstarted/downloads/studio');
                break;

                // setTimeout(() => {
                //   this.toastr.info(this.translocoService.translate('toast.loggedLegacy', {}, this.translocoService.getActiveLang()));
                // }, 100);
                // setTimeout(() => {
                //   this.toastr.warning(this.translocoService.translate('toast.redirectLite', {}, this.translocoService.getActiveLang()));
                // }, 2000);
                // setTimeout(() => {
                //   Lib.AlertOnLeaveOff();
                //   window.location.replace(this.appSettings.studioLiteLegacy);
                // }, 5000);
            }
            case EnumStudioType.PRO: {
                // Studio Pro can be migrated. Migration is a dev/local-only tool, so when it's
                // available land straight on the full-page /migrate route; otherwise fall back to
                // the legacy download page. (Cancel on the migrate page returns to /home.)
                if (isMigrationAvailable() && isAccountMigratable(this.user$.value)) {
                    this.router.navigateByUrl('/migrate');
                } else {
                    this.router.navigateByUrl('/getstarted/downloads/studio');
                }
                break;
            }
            case EnumStudioType.SIGN_STUDIO: {
                this.toastr.clear();
                this.navigateOverview();
                // this.router.navigateByUrl(this.redirectUrl || '/');
                break;
            }
        }
    }

    public navigateOverview(): void {
        this.router.navigateByUrl('/home');
        // this.cod(
        //     combineLatest([
        //         this.accessService.getGroupsCollection("SignStudio Editor"),
        //         this.accessService.getGroupsCollection("SignStudio Dashboard"),
        //         this.accessService.getGroupsCollection("SignStudio Players")])
        //         .pipe(take(1))
        //         .subscribe(([signStudioEditor, signStudioDashboard, signStudioPlayers]) => {
        //                 /** didn't find the user assigned privileges group so must have been recently added XML, just route to overview **/
        //                 if (isNullOrUndef(signStudioEditor) || isNullOrUndef(signStudioDashboard) || isNullOrUndef(signStudioPlayers)) {
        //                     return this.router.navigateByUrl('/home');
        //                 }
        //                 const enableDashboard = this.localStorage.getStorageValConfig('enableDashboard');
        //                 /** dashboard allowed **/
        //                 if (signStudioDashboard.$.visible === '1' && enableDashboard === '1') {
        //                     return this.router.navigateByUrl('/home');
        //                 }
        //                 /** if editor allowed go there **/
        //                 if (signStudioEditor.$.visible === '1') {
        //                     return this.router.navigateByUrl('editor/list');
        //                 }
        //                 /** if players allowed go there **/
        //                 if (signStudioPlayers.$.visible === '1') {
        //                     return this.router.navigateByUrl('stations');
        //                 }
        //             }
        //         )
        // );
    }

    public backToLogin() {
        this.router.navigateByUrl('/login/' + _.uniqueId());
        setTimeout(() => {
            this.storeSelector.dispatch(actionLoginViewMode({loginViewMode: EnumLoginViewMode.LOGIN}));
        }, 10);
    }

    /**
     * Create User from Social Login
     *
     * Called when user logs in via social provider (Google, Facebook, Microsoft, Amazon).
     * Checks if account exists - if not, creates new account.
     *
     * Social Provider Mapping:
     * - Google/Facebook: profile.access -> socialUser.email
     * - Microsoft/LDAP: profile.ldap -> msUser + msPassword
     * - Amazon: profile.iss -> profile.email
     * - Other: profile.emails[0].value
     *
     * Flow:
     * 1. Get template ID from localStorage (selected during signup)
     * 2. If account exists: Return (loginSuccess() handles existing accounts)
     * 3. Extract email from provider-specific profile structure
     * 4. Generate random password (except LDAP which provides password)
     * 5. Call createUser() with extracted credentials
     *
     * @param i_socialServerReply - Server response with social profile data
     */
    public createUserSocial(i_socialServerReply: ISocialServerReply | IAmazonLogin) {
        const templateId = this.localStorage.getStorageValConfig('socialUserPendingTemplateId');
        if (i_socialServerReply.exists) return;
        if (i_socialServerReply.profile) {
            let userName;
            let password = null;
            if (i_socialServerReply.profile?.access) {
                userName = i_socialServerReply.socialUser.email;
            } else if (i_socialServerReply.profile['ldap']) {
                userName = i_socialServerReply.msUser;
                password = i_socialServerReply.msPassword;
            } else if (i_socialServerReply.profile['iss']) {
                userName = i_socialServerReply.profile['email'];
            } else {
                userName = i_socialServerReply.profile['emails'][0].value;
            }
            this.toastr.info('creating account...');
            this.createUser('', userName, password, userName, templateId, this.resellerId, EnumStudioType.SIGN_STUDIO);
        } else {
            this.toastr.error(this.translocoService.translate('toast.probSocialRep', {}, this.translocoService.getActiveLang()));
        }
    }

    /**
     * Create User Account
     *
     * Creates new user account on the server. Used for both traditional
     * signup and social login account creation.
     *
     * @param businessName - Company name (defaults to "DigitalSignage" if empty)
     * @param userName - Email address (used as login username)
     * @param password - User password (auto-generated if empty, e.g., social login)
     * @param contactEmail - Contact email (usually same as userName)
     * @param templateSampleId - Template account ID to clone resources from
     * @param resellerId - Reseller/white-label identifier
     * @param studioType - Type of studio (LITE, PRO, SIGN_STUDIO)
     *
     * Flow:
     * 1. Generate random password if not provided
     * 2. Default businessName if empty
     * 3. Dispatch actionAccountCommandsRequest with CREATE serverCall
     * 4. Server creates account and copies template resources
     * 5. accountCreated() callback handles success
     *
     * Note: First/last name, phone, address fields intentionally left empty
     *       (can be updated later in account settings)
     */
    public createUser(businessName: string, userName: string, password: string, contactEmail: string, templateSampleId: string, resellerId: string, studioType: EnumStudioType) {
        if (isNullOrUndefOrEmpty(password)) password = Lib.RandomString();
        if (isNullOrUndefOrEmpty(businessName)) businessName = 'DigitalSignage';
        this.storeSelector.appStore.dispatch(actionAccountCommandsRequest({
                accountRequest: {
                    createCustomerAccount: {
                        businessName,
                        userName,
                        password,
                        resellerId,
                        studioType,
                        templateBusinessId: templateSampleId,
                        firstName: '',
                        lastName: '',
                        contactEmail,
                        workPhone: '',
                        cellPhone: '',
                        address: '',
                        city: '',
                        state: '',
                        contry: '',
                        zipcode: '',
                        emailUpdates: '',
                        callback: ''
                    },
                    createAccount: {
                        templateId: templateSampleId
                    },
                    serverCall: "CREATE"
                }
            })
        );
    }

    /**
     * Get Empty User Object
     *
     * Returns IUser object with all fields set to null.
     * Used for logout and login failed states.
     *
     * @returns IUser with null values for all properties
     */
    public getEmptyUser(): IUser {
        return {
            loginStatus: null,
            businessId: null,
            ip: null,
            businessName: null,
            accountVersion: null,
            pricingMethod: null,
            customStudio: null,
            payerId: null,
            webApiBaseUrl: null,
            webApiResellerBaseUrl: null,
            adnetUrls: null,
            username: null,
            avatar: null,
            avatarURL: null,
            email: null,
            status: null,
            description: null,
            statusLabel: null,
            isEnabled: null,
            studioType: null,
            migrationStatus: null,
            migrationDate: null,
            createdAt: null,
            updatedAt: null,
            passwordNew: null,
            rememberMe: null,
            accessToken: null,
            name: null,
            cardNumber: null,
            cardType: null,
            businessDomain: null,
            resellerId: null,
            enterprise: null,
            dashboard: null,
            plus: null,
            applicationId: null,
            mediaCloud: null,
            localStoreConfigVersion: null,
            accessMask: null,
            accountStatus: null,
            accountStatusReseller: null,
            webToken: null,
            archiveState: null,
            sourceId: null,
            fromTemplateId: null,
            maxMonitors: null,
            maxDataStorage: null,
            allowSharing: null,
            privilegeId: null,
            groupsPrivileges: null
        };
    }
}

